import {Button} from "primereact/button";
import {Card} from "primereact/card";
import {ConfirmDialog} from "primereact/confirmdialog";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {ProgressSpinner} from "primereact/progressspinner";
import {type ChangeEvent, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";

import type {GeoRefAddress} from "@reva/shared/geo-reference";
import type {AvmSpecSegment} from "@reva/shared/reva-spec";
import type {ValueOrderDocument} from "@reva/shared/value-order";

import ImmodropErrorMessage from "../../component/ImmodropErrorMessage";
import LoadingIndicator from "../../component/LoadingIndicator";
import ValueLendingValueInfo from "../../component/ValueLendingValueInfo";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {useHeightToViewportBottom} from "../../hooks/useHeightToViewportBottom";
import {selectAddressData} from "../../redux/addressSlice";
import {
    selectDocuments,
    selectInitialized as selectDocumentsInitialized,
    selectLoading as selectDocumentsLoading,
} from "../../redux/documentsSlice";
import {downloadDocumentAsync, loadDocumentsAsync, reloadDocumentsIfMissing} from "../../redux/documentsThunk";
import {
    selectAddressError,
    selectAddressLoading,
    selectImmodropLoading,
    selectImmodropQueryError,
    selectImportAddress,
    selectImportJson,
} from "../../redux/importSlice";
import {
    abortImmodropImportAsync,
    applyImmodropImportToCurrentValuation,
    deleteImmodropLayerAsync,
    discardImportAsync,
    queryImmodropAsync,
    queryImmodropFileAsync,
} from "../../redux/importThunk";
import {selectAllImmodropParams, selectAllInspectionParams, selectSegment} from "../../redux/objectParametersSlice";
import {updateSegmentAndValidateAndSubmitFormThunk} from "../../redux/objectParametersThunk";
import {selectPersistenceErrorForSingleParam} from "../../redux/persistenceSelectors";
import {
    removePersistenceSingleParamError,
    selectIsSnapshot,
    selectLifeCycleStateFinal,
    selectPersistenceInitialized,
    selectPersistenceLoading,
    selectRevaId,
} from "../../redux/persistenceSlice";
import {resetPersistenceToLastValidResponse} from "../../redux/persistenceThunk";
import {useRevaDispatch, useRevaSelector} from "../../redux/revaHooks";
import {selectSpecApiParamInputs, selectLoading as selectSpecLoading} from "../../redux/specSlice";
import {compareAddresses, isAddressContradictory} from "../../util/addressConflict";
import {formatDate, formatDateAndTime, formatTime} from "../../util/dateUtil";
import {
    exposePortalName,
    hasImmodropValues,
    readExposeSegment,
    readImmodropSource,
    summarizeImmodropImport,
} from "../../util/immodropSource";
import ErrorMessageContainer from "./ErrorMessageContainer";
import {ObjectOutputGrid} from "./ObjectOutputGrid";
import PersistenceFinalMessage from "./PersistenceFinalMessage";
import SnapshotMessage from "./SnapshotMessage";

/**
 * Lädt das archivierte Exposé-PDF und zeigt es als iframe an (analog EnergyCertificatePreview in
 * TabObjectEnergy.jsx). Die Object-URL wird beim Dokumentwechsel/Unmount wieder freigegeben.
 */
function ExposePdfPreview({document}: {document: ValueOrderDocument}) {
    const {t} = useTranslation();
    const dispatch = useRevaDispatch();
    const [url, setUrl] = useState<string | undefined>(undefined);
    const [failed, setFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const frameRef = useRef<HTMLIFrameElement>(null);
    const frameHeight = useHeightToViewportBottom(frameRef, url != null);
    const {valueOrderGuid, documentId, extension, filename} = document;

    // Auf die IDs statt auf das Objekt: jedes Nachladen der Dokumentenliste liefert neue Referenzen.
    useEffect(() => {
        let objectUrl: string | undefined;
        let cancelled = false;
        setUrl(undefined);
        setFailed(false);
        dispatch(downloadDocumentAsync({valueOrderGuid, documentId, extension}))
            .then((blob) => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setUrl(objectUrl);
            })
            .catch((e: unknown) => {
                console.error(e);
                if (!cancelled) setFailed(true);
            });
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [valueOrderGuid, documentId, extension, attempt, dispatch]);

    if (extension?.toLowerCase() !== "pdf") return null;
    if (failed)
        return (
            <div>
                <p className="text-muted-color">{t("object.immodrop.preview.downloadFailed")}</p>
                <Button
                    label={t("object.immodrop.preview.retry")}
                    icon="pi pi-refresh"
                    outlined
                    onClick={() => setAttempt((n) => n + 1)}
                    type="button"
                />
            </div>
        );
    if (!url) return <LoadingIndicator loading={true} />;
    return (
        <iframe
            ref={frameRef}
            title={filename}
            src={url}
            width="100%"
            height={frameHeight}
            style={{border: 0, display: "block"}}
        />
    );
}

/**
 * Rechte Seite eines URL-Imports: Portale verbieten das Einbetten, also Herkunft, Link und Stand statt Vorschau.
 */
function ExposeUrlPreview({url, importedAt}: {url: string; importedAt: string}) {
    const {t, language} = useRevaTranslation();
    const portal = exposePortalName(url);
    return (
        <Card title={t("object.immodrop.preview.url.title", {portal: portal ?? url})}>
            {portal ? (
                <a href={url} target="_blank" rel="noopener noreferrer" className="break-all">
                    {url}
                </a>
            ) : (
                <span className="break-all">{url}</span>
            )}
            <p className="mt-4 mb-2">
                {t("object.immodrop.preview.url.importedAt", {date: formatDateAndTime(importedAt, language)})}
            </p>
            <p className="mt-0 mb-4 text-muted-color">{t("object.immodrop.preview.url.hint")}</p>
            {portal && (
                <Button
                    label={t("object.immodrop.preview.url.open", {portal})}
                    icon="pi pi-external-link"
                    onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                    type="button"
                />
            )}
        </Card>
    );
}

/**
 * Inhalt des Konfliktdialogs: beide Adressen nebeneinander, die widersprüchlichen Teile markiert.
 */
function AddressConflictMessage({imported, current}: {imported: GeoRefAddress | null; current: GeoRefAddress | null}) {
    const {t, language} = useRevaTranslation();
    const displayName = (address: GeoRefAddress | null) =>
        address ? (language === "de" ? address.displayNameDE : address.displayNameEN) : "–";
    const cell = "py-2 pr-4 text-left align-top border-b border-surface-200";
    return (
        <div className="max-w-[40rem]">
            <p className="mt-0 mb-3">{t("object.immodrop.addressConflict.intro")}</p>
            <table className="w-full border-collapse mb-4">
                <thead>
                    <tr>
                        <th className={cell} />
                        <th className={cell}>{t("object.immodrop.addressConflict.columns.expose")}</th>
                        <th className={cell}>{t("object.immodrop.addressConflict.columns.valuation")}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className={`${cell} text-muted-color`}>
                            {t("object.immodrop.addressConflict.fields.match")}
                        </td>
                        <td className={cell}>{displayName(imported)}</td>
                        <td className={cell}>{displayName(current)}</td>
                    </tr>
                    {compareAddresses(imported, current).map((row) => (
                        <tr key={row.field} className={row.differs ? "text-red-500 font-semibold" : undefined}>
                            <td className={`${cell} ${row.differs ? "" : "text-muted-color"}`}>
                                {row.differs && <i className="pi pi-exclamation-circle mr-2" />}
                                {t(`object.immodrop.addressConflict.fields.${row.field}`)}
                            </td>
                            <td className={cell}>{row.imported || "–"}</td>
                            <td className={cell}>{row.current || "–"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <p className="m-0 text-muted-color">{t("object.immodrop.addressConflict.note")}</p>
        </div>
    );
}

/**
 * Hinweis, wenn das Exposé ein anderes Segment beschreibt als das der Bewertung, samt Übernahme. Die
 * Übernahme läuft über denselben Weg wie die Segmentauswahl im Überblick und setzt deshalb auch die dort
 * überschriebenen Werte zurück. Die Exposé-Schicht bleibt dabei erhalten (#19159).
 */
function ExposeSegmentMismatch({
    exposeSegment,
    valuationSegment,
    disabled,
}: {
    exposeSegment: AvmSpecSegment;
    valuationSegment: string;
    disabled: boolean;
}) {
    const {t} = useTranslation();
    const dispatch = useRevaDispatch();
    const [showConfirm, setShowConfirm] = useState(false);
    const [comment, setComment] = useState("");
    // Das Backend verlangt einen Kommentar, wenn die Besichtigung ein anderes Segment trägt.
    const inspectionSegment = (useRevaSelector(selectAllInspectionParams) as Record<string, unknown> | null)?.segment;
    const commentRequired = inspectionSegment != null && inspectionSegment !== exposeSegment;
    const expose = t(`object.segments.${exposeSegment}`);

    const onAdopt = () => {
        setShowConfirm(false);
        void dispatch(updateSegmentAndValidateAndSubmitFormThunk(exposeSegment, comment.trim() || null));
    };

    return (
        <div className="flex items-center gap-4 mb-4 p-3 rounded-border border-1 border-[var(--immodrop-color)]">
            <i className="pi pi-info-circle text-[var(--immodrop-color)]" />
            <span className="flex-1 min-w-0">
                {t("object.immodrop.segment.mismatch", {expose, valuation: t(`object.segments.${valuationSegment}`)})}
            </span>
            <Button
                label={t("object.immodrop.segment.adopt")}
                icon="pi pi-arrow-right-arrow-left"
                outlined
                className="shrink-0"
                disabled={disabled}
                onClick={() => {
                    setComment("");
                    setShowConfirm(true);
                }}
                type="button"
            />
            <Dialog
                visible={showConfirm}
                modal
                draggable={false}
                resizable={false}
                header={t("object.immodrop.segment.confirmHeader")}
                onHide={() => setShowConfirm(false)}
                style={{maxWidth: "40rem"}}
                footer={
                    <>
                        <Button
                            label={t("object.immodrop.segment.cancel")}
                            outlined
                            onClick={() => setShowConfirm(false)}
                            type="button"
                        />
                        <Button
                            label={t("object.immodrop.segment.confirm", {segment: expose})}
                            icon="pi pi-check"
                            disabled={commentRequired && !comment.trim()}
                            onClick={onAdopt}
                            type="button"
                        />
                    </>
                }
            >
                <p className="mt-0">{t("object.immodrop.segment.confirmText", {segment: expose})}</p>
                <label htmlFor="exposeSegmentComment" className="block mb-2">
                    {t(
                        commentRequired
                            ? "object.immodrop.segment.commentRequired"
                            : "object.immodrop.segment.commentOptional",
                    )}
                </label>
                <InputTextarea
                    id="exposeSegmentComment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={3}
                    className="w-full"
                />
            </Dialog>
        </div>
    );
}

/**
 * #19159: read-only reference tab for the last Immodrop URL/PDF scan (lowest-priority fallback
 * source, see resolveActiveParameterValue). Solange die Schicht leer ist, steckt der Tab hinter dem
 * "+"-Tab der Tab-Liste (selectHasSubtabParams.immodrop, PageObject.jsx). Also offers scanning/uploading for the
 * CURRENTLY loaded valuation (not just at creation, s. AutoDetectMultiInput/DialogImport): the
 * found address is never taken over here, only compared against the current valuation's address.
 * The import runs through without asking when the exposé has no address, its address cannot be
 * geo-referenced, or it does not contradict the valuation's address (isAddressContradictory);
 * only a contradicting address opens the confirmation dialog.
 */
export default function TabObjectImmodrop() {
    const {t, language} = useRevaTranslation();
    const dispatch = useRevaDispatch();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const revaId = useRevaSelector(selectRevaId);
    const isFinal = useRevaSelector(selectLifeCycleStateFinal);
    const isSnapshot = useRevaSelector(selectIsSnapshot);
    const isReadOnly = isFinal || isSnapshot;

    const immodropParams = useRevaSelector(selectAllImmodropParams);
    const currentAddress = useRevaSelector(selectAddressData)?.address ?? null;

    const immodropLoading = useRevaSelector(selectImmodropLoading);
    const addressLoading = useRevaSelector(selectAddressLoading);
    const immodropQueryError = useRevaSelector(selectImmodropQueryError);
    const addressQueryError = useRevaSelector(selectAddressError);
    const importAddress = useRevaSelector(selectImportAddress);
    const importJson = useRevaSelector(selectImportJson);

    const loading = immodropLoading || addressLoading;

    const specLoading = useRevaSelector(selectSpecLoading);
    const persistenceLoading = useRevaSelector(selectPersistenceLoading);
    const pageLoading = specLoading || persistenceLoading;

    const [url, setUrl] = useState("");
    const [showAddressConflict, setShowAddressConflict] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    // Übernahme-Phase (Archivieren + Persistieren) — anders als Extraktion und Adressauflösung hat sie
    // kein Flag im Store, hält aber den Fortschrittsdialog oben, damit niemand halb geschriebene
    // Schichten hinter sich lässt (#19159).
    const [applying, setApplying] = useState(false);
    // Für den Fortschrittstext: eine URL wird ausgelesen, ein PDF hochgeladen.
    const [scanningUrl, setScanningUrl] = useState(false);

    // Herkunft des zuletzt eingelesenen Exposés — reist in der Schicht selbst mit (#19159).
    const source = readImmodropSource(immodropParams);
    // Die Schicht wird im Segment des Exposés gezeigt, sonst wären z.B. MFH-Objekttypen in einer
    // EZFH-Bewertung „ungültig".
    const segment = useRevaSelector(selectSegment);
    const exposeSegment = readExposeSegment(immodropParams);
    const specInputs = useRevaSelector(selectSpecApiParamInputs);
    const displaySpecParams = specInputs?.[exposeSegment ?? segment];
    // Bilanz des Imports: gezählt wird, was das Grid darunter zeigt; als nicht übernommen gilt nur, was
    // weder das Segment des Exposés noch das der Bewertung kennt.
    const {importedCount} = summarizeImmodropImport(immodropParams, displaySpecParams);
    const {ignored} = summarizeImmodropImport(immodropParams, {...specInputs?.[segment], ...displaySpecParams});
    // Ein Segmentwechsel, den das Backend mangels Kommentar ablehnt, bleibt lokal stehen; sonst sähe man ihn
    // hier gar nicht, der Hinweis steckt nur am Segmentfeld im Überblick.
    const segmentSaveError = useRevaSelector(selectPersistenceErrorForSingleParam("segment"));
    const documents = useRevaSelector(selectDocuments);
    const exposeDocument = source?.documentId
        ? (documents ?? []).find((document) => document.documentId === source.documentId)
        : undefined;
    // Jedes Nachladen der Dokumentenliste setzt sie kurz auf null; die Vorschau soll dabei stehen bleiben
    // statt zu flackern und das PDF neu zu laden.
    const [lastExposeDocument, setLastExposeDocument] = useState<ValueOrderDocument | undefined>(undefined);
    if (exposeDocument && exposeDocument !== lastExposeDocument) setLastExposeDocument(exposeDocument);
    const previewDocument =
        exposeDocument ??
        (source?.documentId && lastExposeDocument?.documentId === source.documentId ? lastExposeDocument : undefined);
    const documentsInitialized = useRevaSelector(selectDocumentsInitialized);
    const documentsLoading = useRevaSelector(selectDocumentsLoading);
    const persistenceInitialized = useRevaSelector(selectPersistenceInitialized);

    // Wie die anderen Objekt-Tabs mit Dokumenten: beim direkten Aufruf ist die Liste noch nie geladen worden.
    // Nur bei archiviertem Exposé, sonst liefe der Abruf für Bewertungen ohne Auftrag in einen Fehler.
    useEffect(() => {
        if (source?.documentId && persistenceInitialized && !documentsInitialized && !documentsLoading) {
            void dispatch(loadDocumentsAsync());
        }
    }, [source?.documentId, persistenceInitialized, documentsInitialized, documentsLoading, dispatch]);

    // Die Dokumentenliste kennt das eben archivierte Exposé erst nach einem Nachladen; ohne sie gäbe es
    // nur den Dateinamen ohne Link.
    // Nur ein Versuch je Dokument: fehlt es auch danach im Auftrag, bliebe es sonst eine Endlosschleife.
    const reloadedForDocumentIdRef = useRef<string | null>(null);
    useEffect(() => {
        const documentId = source?.documentId;
        if (!documentId || exposeDocument || !documentsInitialized || documentsLoading) return;
        if (reloadedForDocumentIdRef.current === documentId) return;
        reloadedForDocumentIdRef.current = documentId;
        void dispatch(reloadDocumentsIfMissing(null));
    }, [source?.documentId, exposeDocument, documentsInitialized, documentsLoading, dispatch]);

    // Discard any import state left over from a different flow
    useEffect(() => {
        dispatch(discardImportAsync());
        setShowAddressConflict(false);
    }, [revaId, dispatch]);

    // Once the scanned/uploaded result is there and its address resolution has finished (found, failed
    // or not attempted because the exposé has no address), apply the import — unless the found address
    // contradicts the valuation's address, then ask first (#19159). applyingRef prevents a second run
    // while the async apply is still pending (it ends with clear(), which resets importJson).
    const applyingRef = useRef(false);
    useEffect(() => {
        if (!importJson || addressLoading || applyingRef.current) return;
        const addressResolved = importAddress != null || addressQueryError != null;
        if (!addressResolved) return;
        if (isAddressContradictory(importAddress, currentAddress)) {
            setShowAddressConflict(true);
            return;
        }
        applyingRef.current = true;
        setApplying(true);
        void dispatch(applyImmodropImportToCurrentValuation()).finally(() => {
            applyingRef.current = false;
            setApplying(false);
        });
    }, [importJson, importAddress, addressQueryError, addressLoading, currentAddress, dispatch]);

    const onScanUrl = () => {
        if (!url.trim()) return;
        setScanningUrl(true);
        void dispatch(queryImmodropAsync(url.trim()));
    };

    const onFileSelected = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = "";
        setScanningUrl(false);
        void dispatch(queryImmodropFileAsync(file));
    };

    const onConfirmWithoutAddress = () => {
        setShowAddressConflict(false);
        void dispatch(applyImmodropImportToCurrentValuation());
    };

    const onCancelImport = () => {
        setShowAddressConflict(false);
        dispatch(discardImportAsync());
    };

    const hasImmodropParams = hasImmodropValues(immodropParams);
    // Solange eine Schicht steht, ersetzt die Herkunftszeile die Eingabe: erst löschen, dann neu einlesen.
    // Schichten aus der Zeit vor der Herkunftsangabe haben Werte, aber keine Quelle — auch die müssen
    // löschbar sein, sonst säßen sie fest.
    const hasImmodropLayer = hasImmodropParams || source != null;

    const onDeleteImmodrop = () => {
        setShowDeleteConfirm(false);
        void dispatch(deleteImmodropLayerAsync());
    };

    const onCancelRunningImport = () => {
        applyingRef.current = false;
        setApplying(false);
        setShowAddressConflict(false);
        setUrl("");
        dispatch(abortImmodropImportAsync());
    };

    // Die Übernahme ist nicht mehr abbrechbar: ab hier wird geschrieben.
    const busy = loading || applying;
    const progressI18n = applying
        ? "object.immodrop.progress.applying"
        : addressLoading
          ? "object.immodrop.progress.resolvingAddress"
          : scanningUrl
            ? "object.immodrop.progress.extractingUrl"
            : "object.immodrop.progress.extracting";

    return (
        <div id="ImmodropPanelTitle">
            <PersistenceFinalMessage />
            <SnapshotMessage />
            <ValueLendingValueInfo />
            <ErrorMessageContainer />
            <LoadingIndicator loading={pageLoading} className="h-full" />

            {!pageLoading && (
                <>
                    <input
                        type="file"
                        accept=".pdf"
                        ref={fileInputRef}
                        style={{display: "none"}}
                        onChange={onFileSelected}
                    />
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-6">
                            {hasImmodropLayer && (
                                <div className="flex items-start gap-4 mb-4">
                                    <span className="flex-1 min-w-0 title-font-size">
                                        {!source ? (
                                            <>
                                                {t("object.immodrop.source.unknown")}
                                                <div>
                                                    {t("object.immodrop.source.importedCount", {count: importedCount})}
                                                </div>
                                            </>
                                        ) : source.url ? (
                                            t("object.immodrop.source.fromUrl", {
                                                count: importedCount,
                                                portal: exposePortalName(source.url) ?? source.url,
                                                date: formatDate(source.importedAt, language),
                                                time: formatTime(source.importedAt, language),
                                            })
                                        ) : (
                                            t("object.immodrop.source.fromFile", {
                                                count: importedCount,
                                                filename: source.filename ?? t("object.immodrop.source.unknownFile"),
                                                date: formatDate(source.importedAt, language),
                                                time: formatTime(source.importedAt, language),
                                            })
                                        )}
                                    </span>
                                    <Button
                                        label={t("object.immodrop.source.delete")}
                                        icon="pi pi-trash"
                                        severity="danger"
                                        outlined
                                        className="shrink-0"
                                        loading={loading}
                                        disabled={isReadOnly}
                                        onClick={() => setShowDeleteConfirm(true)}
                                        type="button"
                                    />
                                </div>
                            )}

                            {!hasImmodropLayer && (
                                <>
                                    {/* Bewusst auf Überschriftengröße: der Satz ist der einzige Wegweiser im leeren Tab (#19159). */}
                                    <p className="mb-4 title-font-size">{t("object.immodrop.intro")}</p>
                                    <div className="flex items-center gap-4 mb-4">
                                        <InputText
                                            value={url}
                                            onChange={(e) => setUrl(e.target.value)}
                                            placeholder={t("object.immodrop.scanUrlPlaceholder")}
                                            disabled={loading || isReadOnly}
                                            className="flex-1"
                                        />
                                        <Button
                                            label={t("object.immodrop.scanUrlButton")}
                                            icon="pi pi-search"
                                            loading={loading}
                                            disabled={isReadOnly || !url.trim()}
                                            onClick={onScanUrl}
                                            type="button"
                                        />
                                        <Button
                                            label={t("object.immodrop.uploadButton")}
                                            icon="pi pi-file-pdf"
                                            loading={loading}
                                            disabled={isReadOnly}
                                            onClick={() => fileInputRef.current?.click()}
                                            type="button"
                                        />
                                    </div>
                                </>
                            )}

                            {segmentSaveError && (
                                <div className="flex items-center gap-4 mb-4 p-3 rounded-border border-1 border-red-500">
                                    <i className="pi pi-exclamation-triangle text-red-500" />
                                    <span className="flex-1 min-w-0">{t("object.immodrop.segment.saveFailed")}</span>
                                    <Button
                                        label={t("object.immodrop.segment.undo")}
                                        icon="pi pi-undo"
                                        outlined
                                        className="shrink-0"
                                        onClick={() => {
                                            // Den Fehler mit abräumen, sonst bleibt dieses Banner
                                            // stehen: Geleert wird er sonst nur vom nächsten
                                            // Schreibvorgang, und das Zurücksetzen schreibt nichts.
                                            void dispatch(resetPersistenceToLastValidResponse());
                                            dispatch(removePersistenceSingleParamError());
                                        }}
                                        type="button"
                                    />
                                </div>
                            )}

                            {hasImmodropParams && exposeSegment && exposeSegment !== segment && (
                                <ExposeSegmentMismatch
                                    exposeSegment={exposeSegment}
                                    valuationSegment={segment}
                                    disabled={isReadOnly}
                                />
                            )}

                            {hasImmodropParams ? (
                                <div className="ImmodropOutputGrid">
                                    <ObjectOutputGrid
                                        outputData={
                                            exposeSegment ? {...immodropParams, segment: exposeSegment} : immodropParams
                                        }
                                        fullsize={false}
                                        segmentOverride={exposeSegment}
                                    />
                                </div>
                            ) : (
                                // Ohne Schicht erklärt bereits der Einleitungssatz oben, was hier hingehört.
                                hasImmodropLayer && <p>{t("object.immodrop.emptyState")}</p>
                            )}

                            {ignored.length > 0 && (
                                <p className="mt-8 text-muted-color">
                                    {t("object.immodrop.source.ignored", {
                                        count: ignored.length,
                                        params: ignored.map((param) => `„${param}"`).join(", "),
                                    })}
                                </p>
                            )}
                        </div>

                        <div className="col-span-6">
                            {previewDocument ? (
                                <ExposePdfPreview key={previewDocument.documentId} document={previewDocument} />
                            ) : source?.documentId && (!documentsInitialized || documentsLoading) ? (
                                <LoadingIndicator loading={true} />
                            ) : source?.url ? (
                                <ExposeUrlPreview url={source.url} importedAt={source.importedAt} />
                            ) : (
                                source && <p className="text-muted-color">{t("object.immodrop.preview.notArchived")}</p>
                            )}
                        </div>
                    </div>

                    {/* Sperrt die Oberfläche (auch die Tab-Leiste), solange etwas läuft — nur Abbrechen geht (#19159). */}
                    <Dialog
                        visible={busy}
                        modal
                        closable={false}
                        draggable={false}
                        resizable={false}
                        closeOnEscape={false}
                        header={t("object.immodrop.progress.header")}
                        onHide={() => undefined}
                        footer={
                            applying ? null : (
                                <Button
                                    label={t("object.immodrop.progress.cancel")}
                                    icon="pi pi-times"
                                    outlined
                                    onClick={onCancelRunningImport}
                                    type="button"
                                />
                            )
                        }
                    >
                        <div className="flex items-center gap-4">
                            <ProgressSpinner style={{width: "2.5rem", height: "2.5rem"}} strokeWidth="4" />
                            <span>{t(progressI18n)}</span>
                        </div>
                    </Dialog>

                    <ImmodropErrorMessage error={immodropQueryError} />

                    <ConfirmDialog
                        visible={showAddressConflict}
                        header={t("object.immodrop.addressConflict.header")}
                        message={<AddressConflictMessage imported={importAddress} current={currentAddress} />}
                        icon="pi pi-exclamation-triangle"
                        acceptLabel={t("object.immodrop.addressConflict.importWithoutAddress")}
                        rejectLabel={t("object.immodrop.addressConflict.cancel")}
                        accept={onConfirmWithoutAddress}
                        reject={onCancelImport}
                        onHide={onCancelImport}
                    />

                    <ConfirmDialog
                        visible={showDeleteConfirm}
                        header={t("object.immodrop.deleteConfirm.header")}
                        message={t("object.immodrop.deleteConfirm.message")}
                        icon="pi pi-exclamation-triangle"
                        acceptLabel={t("object.immodrop.deleteConfirm.accept")}
                        rejectLabel={t("object.immodrop.deleteConfirm.cancel")}
                        accept={onDeleteImmodrop}
                        reject={() => setShowDeleteConfirm(false)}
                        onHide={() => setShowDeleteConfirm(false)}
                    />
                </>
            )}
        </div>
    );
}
